/**
 * Batch 18-D17 — Contextual Adjacency Boost Helper.
 *
 * Pure helper that, given a ticket's job IDs and an artifact's job
 * IDs, looks up a per-pair boost from a pre-loaded job adjacency map
 * and returns the FIRST match's boost + the related job ID. Used by
 * `contextualTrainingScorer.scoreArtifactsForTicket` to add an
 * additive boost when the artifact is mapped to a job that's
 * adjacent to one of the ticket's jobs.
 *
 * Numeric behavior is preserved BIT-FOR-BIT from the prior contextual
 * scorer version (i.e. the values the gate `if (adjacencyBoost > 0)`
 * sees at the call site are byte-identical):
 *
 *   - Empty `ticketJobIds` → `0`.
 *   - Empty `adjacencyMap` → `0`.
 *   - Empty `artifactJobIds` → `0` (via the inner `.includes` always
 *     returning false; no early short-circuit).
 *   - Scans `ticketJobIds` in order. For each, filters
 *     `adjacencyMap` to entries whose `sourceJobId` matches, then
 *     iterates them in their original `adjacencyMap` order looking
 *     for the first `relatedJobId` that appears in `artifactJobIds`.
 *   - Returns the FIRST match's `boost` value.
 *   - Does NOT accumulate boosts across multiple matching
 *     adjacencies. Does NOT sort `adjacencyMap`. Does NOT defensively
 *     coerce a `null` boost — trusts the declared `AdjacencyInfo`
 *     type.
 *
 * 18-D18: removed the previously-vestigial `matchedJobId` return field.
 *   - 18-D17 preserved it for behavior-parity at extraction time and
 *     pinned its identity (equals the matching adjacency's
 *     `relatedJobId`, not `sourceJobId`) in tests. After that landed,
 *     consumer-set discovery confirmed the field had never been read
 *     anywhere in production — the scorer call site only ever
 *     destructured `{ boost }`. 18-D18 trims the helper signature to
 *     return a plain `number` and updates the call site to a direct
 *     assignment. Numeric output for every input path is unchanged.
 *   - This mirrors the 18-D12 → 18-D13 cleanup arc for the contextual
 *     explanation helper's `durationBoosted` parameter exactly:
 *     extract verbatim, pin behavior, then trim the now-known-inert
 *     surface in the next batch.
 *
 * Module placement: `src/utils/training/scoring/contextualAdjacencyBoost.ts`
 *   - Named with the `contextual` prefix to acknowledge the helper's
 *     specificity to the contextual scorer's `AdjacencyInfo` data
 *     shape. The engine scorer does not use job adjacency; a future
 *     generic-adjacency helper for another surface would live in a
 *     sibling file.
 *
 * Cycle avoidance: the adjacency input type is imported TYPE-ONLY,
 * so TypeScript elides it at compile time and no runtime module cycle
 * is created. Same pattern as 18-D2 (caps.ts), 18-D8
 * (engineExplanation.ts), 18-D12 (contextualExplanation.ts), and
 * 18-D14 (confidenceBadge.ts via a self-contained union).
 *
 * Batch 18-D24: the adjacency input shape is now imported via the
 * shared scoring scaffold (the 18-D22 re-export
 * `TrainingContextualAdjacencyInfo`) rather than directly from the
 * contextual scorer. The scaffold alias is structurally identical to
 * the canonical `AdjacencyInfo` interface declared in
 * `contextualTrainingScorer.ts` — it is a verbatim
 * `export type { AdjacencyInfo as TrainingContextualAdjacencyInfo }`.
 */

import type { TrainingContextualAdjacencyInfo } from '@/utils/training/scoring/types';

/**
 * Calculate adjacency boost based on related jobs (ID-based)
 */
export function calculateContextualAdjacencyBoost(
  artifactJobIds: string[],       // Job IDs from artifact's job_map
  ticketJobIds: string[],         // Job IDs from ticket
  adjacencyMap: TrainingContextualAdjacencyInfo[]   // Pre-loaded adjacency data
): number {
  if (!ticketJobIds.length || !adjacencyMap.length) {
    return 0;
  }

  for (const ticketJobId of ticketJobIds) {
    // Find adjacencies where ticket job is the source
    const adjacencies = adjacencyMap.filter(a => a.sourceJobId === ticketJobId);

    for (const adjacency of adjacencies) {
      if (artifactJobIds.includes(adjacency.relatedJobId)) {
        return adjacency.boost;
      }
    }
  }
  return 0;
}
